import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, ChevronDown, ChevronUp, Image, Loader2, Save, Star, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';

const RecommendedItemsEditor = () => {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [isDirty, setIsDirty] = useState(false);

    const utils = trpc.useUtils();
    const { data: recommendedData, isLoading: isLoadingRecommended } = trpc.bookingFunnelCMS.getRecommendedBookableItems.useQuery();
    const { data: allItemsData, isLoading: isLoadingItems } = trpc.bookingFunnelCMS.getBookableItems.useQuery();

    const saveMutation = trpc.bookingFunnelCMS.setRecommendedBookableItems.useMutation({
        onSuccess: () => {
            toast.success('Empfehlungen gespeichert');
            utils.bookingFunnelCMS.getRecommendedBookableItems.invalidate();
            setIsDirty(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern', { description: error.message });
        },
    });

    useEffect(() => {
        if (recommendedData?.data) {
            setSelectedIds(recommendedData.data.map((r) => r.bookableItem.id));
        }
    }, [recommendedData]);

    const handleToggle = (itemId: string) => {
        setSelectedIds((prev) => {
            if (prev.includes(itemId)) {
                return prev.filter((id) => id !== itemId);
            }
            return [...prev, itemId];
        });
        setIsDirty(true);
    };

    const handleMove = (itemId: string, direction: 'up' | 'down') => {
        setSelectedIds((prev) => {
            const currentIndex = prev.indexOf(itemId);
            if (currentIndex === -1) return prev;

            const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
            if (newIndex < 0 || newIndex >= prev.length) return prev;

            const newOrder = [...prev];
            [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
            return newOrder;
        });
        setIsDirty(true);
    };

    const handleSave = () => {
        saveMutation.mutate({ bookableItemIds: selectedIds });
    };

    const allItems = allItemsData?.data || [];
    // Preserve order from selectedIds
    const selectedItems = selectedIds
        .map((id) => allItems.find((item) => item.id === id))
        .filter((item): item is NonNullable<typeof item> => item !== undefined);
    const availableItems = allItems.filter((item) => !selectedIds.includes(item.id));
    const isLoading = isLoadingRecommended || isLoadingItems;

    if (isLoading) {
        return (
            <div className='space-y-4'>
                <Skeleton className='h-10 w-48' />
                <Skeleton className='h-64 w-full' />
            </div>
        );
    }

    if (allItems.length === 0) {
        return (
            <Card>
                <CardContent className='py-16 text-center'>
                    <div className='flex flex-col items-center gap-4'>
                        <div className='w-16 h-16 rounded-full bg-muted flex items-center justify-center'>
                            <Star className='h-8 w-8 text-muted-foreground' />
                        </div>
                        <div>
                            <p className='font-medium text-lg'>Keine buchbaren Leistungen</p>
                            <p className='text-sm text-muted-foreground mt-1'>
                                Erstellen Sie zuerst buchbare Leistungen im Tab "Buchbare Leistungen",
                                um Empfehlungen festzulegen.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className='space-y-6'>
            {/* Header */}
            <div className='flex justify-between items-start gap-4'>
                <div>
                    <p className='text-sm text-muted-foreground'>
                        Wählen Sie die buchbaren Leistungen aus, die als Empfehlungen für Neukunden angezeigt werden sollen.
                        Diese erscheinen prominent am Anfang der Leistungsauswahl.
                    </p>
                </div>
                <Button onClick={handleSave} disabled={!isDirty || saveMutation.isPending} className='shrink-0'>
                    {saveMutation.isPending ? (
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    ) : (
                        <Save className='mr-2 h-4 w-4' />
                    )}
                    Speichern
                </Button>
            </div>

            {/* Selected Items */}
            {selectedItems.length > 0 && (
                <Card className='border-primary/30 bg-primary/5'>
                    <CardHeader className='pb-3'>
                        <CardTitle className='text-base flex items-center gap-2'>
                            <Star className='h-4 w-4 text-primary fill-primary' />
                            Ausgewählte Empfehlungen ({selectedItems.length})
                        </CardTitle>
                        <CardDescription>
                            Diese Leistungen werden Neukunden empfohlen
                        </CardDescription>
                    </CardHeader>
                    <CardContent className='pt-0'>
                        <div className='space-y-2'>
                            {selectedItems.map((item, index) => (
                                <div
                                    key={item.id}
                                    className='flex items-center gap-3 p-3 bg-white rounded-lg border border-primary/20 transition-all hover:border-primary/40'
                                >
                                    {/* Order controls */}
                                    <div className='flex flex-col shrink-0'>
                                        <Button
                                            variant='ghost'
                                            size='icon'
                                            className='h-6 w-6'
                                            onClick={() => handleMove(item.id, 'up')}
                                            disabled={index === 0 || selectedItems.length <= 1}
                                        >
                                            <ChevronUp className='h-4 w-4' />
                                        </Button>
                                        <Button
                                            variant='ghost'
                                            size='icon'
                                            className='h-6 w-6'
                                            onClick={() => handleMove(item.id, 'down')}
                                            disabled={index === selectedItems.length - 1 || selectedItems.length <= 1}
                                        >
                                            <ChevronDown className='h-4 w-4' />
                                        </Button>
                                    </div>
                                    <Badge variant='outline' className='shrink-0 w-6 h-6 p-0 flex items-center justify-center'>
                                        {index + 1}
                                    </Badge>
                                    {item.images?.[0]?.url ? (
                                        <img
                                            src={item.images[0].url}
                                            alt={item.nameDe}
                                            className='w-10 h-10 object-cover rounded shrink-0'
                                        />
                                    ) : (
                                        <div className='w-10 h-10 bg-muted rounded flex items-center justify-center shrink-0'>
                                            <Image className='h-4 w-4 text-muted-foreground' />
                                        </div>
                                    )}
                                    <div className='flex-1 min-w-0'>
                                        <p className='font-medium truncate'>{item.nameDe}</p>
                                        <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                                            <span>{item.variantOptions?.length || 0} Varianten</span>
                                            <span>•</span>
                                            <span>{item.addOns?.length || 0} Add-Ons</span>
                                        </div>
                                    </div>
                                    <Button
                                        variant='ghost'
                                        size='sm'
                                        className='shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10'
                                        onClick={() => handleToggle(item.id)}
                                    >
                                        <X className='h-4 w-4 mr-1' />
                                        Entfernen
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Available Items */}
            <Card>
                <CardHeader className='pb-3'>
                    <CardTitle className='text-base'>
                        Verfügbare Leistungen ({availableItems.length})
                    </CardTitle>
                    <CardDescription>
                        Klicken Sie auf eine Leistung, um sie zu den Empfehlungen hinzuzufügen
                    </CardDescription>
                </CardHeader>
                <CardContent className='pt-0'>
                    {availableItems.length === 0 ? (
                        <div className='text-center py-8 text-muted-foreground'>
                            <Check className='h-8 w-8 mx-auto mb-2 text-green-500' />
                            <p>Alle Leistungen wurden als Empfehlung ausgewählt</p>
                        </div>
                    ) : (
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
                            {availableItems.map((item) => (
                                <div
                                    key={item.id}
                                    className='flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all hover:border-primary/40 hover:bg-muted/50'
                                    onClick={() => handleToggle(item.id)}
                                >
                                    <Checkbox checked={false} className='pointer-events-none' />
                                    {item.images?.[0]?.url ? (
                                        <img
                                            src={item.images[0].url}
                                            alt={item.nameDe}
                                            className='w-10 h-10 object-cover rounded shrink-0'
                                        />
                                    ) : (
                                        <div className='w-10 h-10 bg-muted rounded flex items-center justify-center shrink-0'>
                                            <Image className='h-4 w-4 text-muted-foreground' />
                                        </div>
                                    )}
                                    <div className='flex-1 min-w-0'>
                                        <p className='font-medium text-sm truncate'>{item.nameDe}</p>
                                        <p className='text-xs text-muted-foreground truncate'>{item.nameEn}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default RecommendedItemsEditor;
